import {
  addMonths,
  differenceInMonths,
  differenceInDays,
  isWithinInterval,
  parseISO,
} from "date-fns";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import firebaseApp from "../firebase/credenciales.js";

const db = getFirestore(firebaseApp);

/**
 * Obtiene los días no trabajados por faltas injustificadas y permisos sin goce para un empleado en un periodo
 * @param {string} empleadoId - ID del empleado
 * @param {string} periodo - Periodo en formato YYYY-MM
 * @returns {Promise<{diasFaltas: number, diasPermisosSinGoce: number}>} - Días no trabajados
 */
export const obtenerDiasNoTrabajados = async (empleadoId, periodo) => {
  try {
    const ocurrenciasRef = collection(db, "pl_ocurrencias");
    const q = query(
      ocurrenciasRef,
      where("empleado_id", "==", empleadoId),
      where("periodo", "==", periodo),
      where("activo", "==", true)
    );
    const snapshot = await getDocs(q);
    let diasFaltas = 0;
    let diasPermisosSinGoce = 0;
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.tipo === "falta" && !data.justificado) {
        diasFaltas += data.dias_afectados || 0;
      } else if (data.tipo === "licencia_sin_goce") {
        diasPermisosSinGoce += data.dias_afectados || 0;
      }
    });
    return { diasFaltas, diasPermisosSinGoce };
  } catch (error) {
    console.error("Error obteniendo días no trabajados:", error);
    return { diasFaltas: 0, diasPermisosSinGoce: 0 };
  }
};

/**
 * Calcula meses computables entre fecha de ingreso y periodo evaluado (proporcional por días)
 * @param {string|Date|Object} fechaIngreso - Fecha de ingreso del empleado (formato ISO, Date o Timestamp Firestore)
 * @param {Object} periodo - Objeto con fechaInicio y fechaFin del periodo
 * @param {string} periodo.fechaInicio - Fecha inicio en formato YYYY-MM-DD
 * @param {string} periodo.fechaFin - Fecha fin en formato YYYY-MM-DD
 * @param {number} diasFaltas - Días de faltas injustificadas
 * @param {number} diasPermisosSinGoce - Días de permisos sin goce
 * @returns {number} - Número de meses computables (hasta 6, proporcional por días)
 */
export const calcularMesesComputables = (fechaIngreso, periodo, diasFaltas = 0, diasPermisosSinGoce = 0) => {
  try {
    // Validaciones exhaustivas de parámetros de entrada
    if (!fechaIngreso) {
      console.warn("calcularMesesComputables: fechaIngreso es requerida");
      return 0;
    }

    if (!periodo || typeof periodo !== 'object') {
      console.warn("calcularMesesComputables: periodo debe ser un objeto válido");
      return 0;
    }

    if (!periodo.fechaInicio || !periodo.fechaFin) {
      console.warn("calcularMesesComputables: periodo debe tener fechaInicio y fechaFin", {
        fechaInicio: periodo?.fechaInicio,
        fechaFin: periodo?.fechaFin
      });
      return 0;
    }

    // Validar formato de fechas del periodo
    const fechaInicioRegex = /^\d{4}-\d{2}-\d{2}$/;
    const fechaFinRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!fechaInicioRegex.test(periodo.fechaInicio) || !fechaFinRegex.test(periodo.fechaFin)) {
      console.warn("calcularMesesComputables: fechas del periodo deben estar en formato YYYY-MM-DD", {
        fechaInicio: periodo.fechaInicio,
        fechaFin: periodo.fechaFin
      });
      return 0;
    }

    // Validar que fechaInicio sea anterior o igual a fechaFin
    const fechaIniParsed = new Date(periodo.fechaInicio);
    const fechaFinParsed = new Date(periodo.fechaFin);

    if (fechaIniParsed > fechaFinParsed) {
      console.warn("calcularMesesComputables: fechaInicio no puede ser posterior a fechaFin", {
        fechaInicio: periodo.fechaInicio,
        fechaFin: periodo.fechaFin
      });
      return 0;
    }

    console.log("🔍 calcularMesesComputables - Parámetros validados correctamente", {
      fechaIngreso: fechaIngreso,
      periodo: periodo
    });

    // Convertir fecha de ingreso si es un Timestamp de Firestore
    let fechaIngresoParsed;
    if (fechaIngreso && typeof fechaIngreso === 'object' && fechaIngreso.toDate) {
      // Es un Timestamp de Firestore
      fechaIngresoParsed = fechaIngreso.toDate();
    } else if (fechaIngreso instanceof Date) {
      // Ya es un objeto Date
      fechaIngresoParsed = fechaIngreso;
    } else if (typeof fechaIngreso === 'string') {
      // Es una string, intentar parsear
      try {
        fechaIngresoParsed = new Date(fechaIngreso);
      } catch (error) {
        console.error("calcularMesesComputables: error al parsear fechaIngreso como string", error);
        return 0;
      }
    } else {
      console.warn("calcularMesesComputables: tipo de fechaIngreso no soportado", typeof fechaIngreso);
      return 0;
    }

    // Verificar que las fechas sean válidas
    if (isNaN(fechaIngresoParsed.getTime()) || isNaN(fechaIniParsed.getTime()) || isNaN(fechaFinParsed.getTime())) {
      console.error("calcularMesesComputables: una o más fechas son inválidas", {
        fechaIngreso: fechaIngreso,
        fechaInicio: periodo.fechaInicio,
        fechaFin: periodo.fechaFin,
        fechaIngresoParsed: fechaIngresoParsed.toString(),
        fechaIniParsed: fechaIniParsed.toString(),
        fechaFinParsed: fechaFinParsed.toString()
      });
      return 0;
    }

    console.log("🔍 calcularMesesComputables - Fechas parseadas:", {
      fechaIng: fechaIngresoParsed.toISOString(),
      fechaIni: fechaIniParsed.toISOString(),
      fechaFin: fechaFinParsed.toISOString()
    });

    // Si ingresó después del fin del periodo, no corresponde
    if (fechaIngresoParsed > fechaFinParsed) {
      console.log("🔍 calcularMesesComputables - Empleado ingresó después del fin del periodo");
      return 0;
    }

    // Calcular días totales en el periodo (incluyendo el día de inicio y fin)
    const diasTotales = Math.floor((fechaFinParsed.getTime() - fechaIniParsed.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    console.log("🔍 calcularMesesComputables - differenceInDays result:", Math.floor((fechaFinParsed.getTime() - fechaIniParsed.getTime()) / (1000 * 60 * 60 * 24)), "fechaFinParsed:", fechaFinParsed, "fechaIniParsed:", fechaIniParsed);

    // Calcular días trabajados
    let diasTrabajados;
    if (fechaIngresoParsed <= fechaIniParsed) {
      // Ingresó antes o en el inicio del periodo: trabajó todos los días
      diasTrabajados = diasTotales;
    } else {
      // Ingresó durante el periodo: trabajó desde fechaIng hasta fechaFin
      diasTrabajados = Math.floor((fechaFinParsed.getTime() - fechaIngresoParsed.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }

    // Restar días no trabajados por faltas y permisos sin goce
    diasTrabajados -= (diasFaltas + diasPermisosSinGoce);
    if (diasTrabajados < 0) diasTrabajados = 0;

    console.log("🔍 calcularMesesComputables - Días totales:", diasTotales, "Días trabajados:", diasTrabajados, "Días faltas:", diasFaltas, "Días permisos sin goce:", diasPermisosSinGoce);

    // Meses computables proporcionales (máximo 6)
    const mesesComputables = (diasTrabajados / diasTotales) * 6;
    const resultado = Math.min(mesesComputables, 6);

    console.log("🔍 calcularMesesComputables - Meses computables calculados:", resultado);
    return resultado;
  } catch (error) {
    console.error("Error al calcular meses computables:", error);
    return 0;
  }
};

/**
 * Valida si un concepto variable cumple con el Principio de Regularidad (3 veces en el semestre)
 * @param {Array} historial - Historial de planillas previas
 * @param {string} bonoId - ID del bono a validar
 * @returns {boolean} - true si cumple con la regularidad
 */
export const validarRegularidadConcepto = (historial, bonoId) => {
  if (!historial || !Array.isArray(historial) || historial.length === 0) return false;
  
  // Contar ocurrencias del bono en el historial
  const ocurrencias = historial.filter(h => {
    // Buscar en el objeto de bonos del detalle histórico
    const bonos = h.bonos || {};
    return Object.keys(bonos).some(id => id.includes(bonoId));
  }).length;

  // Si ya tiene 2 ocurrencias previas en el semestre, el mes actual es la 3era
  return ocurrencias >= 2; 
};

/**
 * Calcula el monto de bonos variables que cumplen regularidad para ser computables
 */
export const calcularBonosVariablesRegulares = (empleado, historial, bonosMap) => {
  if (!empleado.bonos_asignados || !Array.isArray(empleado.bonos_asignados)) return 0;
  
  let totalComputable = 0;
  
  empleado.bonos_asignados.forEach(asignacion => {
    const bonoId = asignacion.bonoId;
    const bonoInfo = bonosMap?.[bonoId];
    
    // Si es un bono remunerativo Y variable (fijo === false)
    if (bonoInfo && bonoInfo.remunerativo && bonoInfo.fijo === false) {
      if (validarRegularidadConcepto(historial, bonoId)) {
        totalComputable += Number(asignacion.monto || 0);
      }
    }
  });
  
  return totalComputable;
};

/**
 * Calcula el total de bonos remunerativos del empleado (aquellos que afectan cálculos como ESSALUD, renta, gratificación, CTS)
 * @param {Object} empleado - Datos del empleado
 * @param {Array} historial - Opcional. Historial para validar regularidad
 * @param {Object} bonosMap - Opcional. Mapa de definiciones de bonos
 * @returns {number} - Monto total de bonos remunerativos
 */
export const calcularBonosRemunerativos = (empleado, historial = [], bonosMap = {}) => {
  try {
    if (!empleado) {
      console.warn("calcularBonosRemunerativos: empleado inválido");
      return 0;
    }
    
    // 1. Bonos fijos (siempre remunerativos por definición en este sistema)
    const bonosFijos = Number(empleado.bonos_fijos || 0);
    const otrosBonosRemunerativos = Number(empleado.otros_bonos_remunerativos || 0);
    const totalFijos = bonosFijos + otrosBonosRemunerativos;

    // 2. Bonos variables que cumplen con el Principio de Regularidad (3/6)
    const totalVariablesRegulares = calcularBonosVariablesRegulares(empleado, historial, bonosMap);

    const totalBonosRemunerativos = totalFijos + totalVariablesRegulares;
    return Number(totalBonosRemunerativos.toFixed(2));
  } catch (error) {
    console.error("Error en calcularBonosRemunerativos:", error);
    return 0;
  }
};

/**
 * Calcula el total de bonos no remunerativos del empleado (no afectan cálculos tributarios/laborales)
 * @param {Object} empleado - Datos del empleado
 * @returns {number} - Monto total de bonos no remunerativos
 */
export const calcularBonosNoRemunerativos = (empleado) => {
  try {
    if (!empleado) {
      console.warn("calcularBonosNoRemunerativos: empleado inválido");
      return 0;
    }
    // Asumir que bonos_adicionales son no remunerativos; ajusta según tu lógica
    const bonosNoRemunerativos = Number(empleado.bonos_adicionales || 0);
    // Agrega otros bonos no remunerativos si existen (e.g., bonos por productividad no computables)
    const otrosBonosNoRemunerativos = Number(empleado.otros_bonos_no_remunerativos || 0);

    const totalBonosNoRemunerativos = bonosNoRemunerativos + otrosBonosNoRemunerativos;
    return Number(totalBonosNoRemunerativos.toFixed(2));
  } catch (error) {
    console.error("Error en calcularBonosNoRemunerativos:", error);
    return 0;
  }
};

/**
 * @param {Object} parametros - Parámetros generales de planilla
 * @param {Array} historial - Opcional. Historial para validar regularidad
 * @param {Object} bonosMap - Opcional. Mapa de definiciones de bonos
 * @returns {number} - Monto de gratificación
 */
export const calcularGratificacion = async (
  empleado,
  regimen,
  periodo,
  parametros,
  historial = [],
  bonosMap = {}
) => {
  console.log("calcularGratificacion llamado con:", {
    empleado: {
      nombres: empleado?.nombres,
      apellidos: empleado?.apellidos,
      sueldo_base: empleado?.sueldo_base,
      fecha_ingreso: empleado?.fecha_ingreso,
      fecha_reingreso: empleado?.fecha_reingreso
    },
    regimen,
    periodo,
    parametros
  });

  try {
    // Validaciones exhaustivas de parámetros de entrada
    if (!empleado || typeof empleado !== 'object') {
      console.warn("calcularGratificacion: empleado debe ser un objeto válido");
      return 0;
    }

    if (!regimen || typeof regimen !== 'object') {
      console.warn("calcularGratificacion: regimen debe ser un objeto válido");
      return 0;
    }

    if (!periodo || typeof periodo !== 'object' || !periodo.fechaInicio || !periodo.fechaFin) {
      console.warn("calcularGratificacion: periodo debe ser un objeto válido con fechaInicio y fechaFin");
      return 0;
    }

    // Validar sueldo base
    const sueldoBase = empleado.sueldo_base;
    let sueldoBaseNum;

    if (typeof sueldoBase === 'string') {
      sueldoBaseNum = parseFloat(sueldoBase);
    } else if (typeof sueldoBase === 'number') {
      sueldoBaseNum = sueldoBase;
    } else {
      console.warn("calcularGratificacion: sueldo_base debe ser un número o string convertible", {
        sueldo_base: sueldoBase,
        tipo: typeof sueldoBase
      });
      return 0;
    }

    if (isNaN(sueldoBaseNum) || sueldoBaseNum <= 0) {
      console.warn("calcularGratificacion: sueldo_base debe ser un número positivo", {
        sueldo_base: sueldoBase,
        sueldo_base_num: sueldoBaseNum
      });
      return 0;
    }

    // Actualizar el empleado con el sueldo_base convertido
    empleado.sueldo_base = sueldoBaseNum;

    // Validar configuración de gratificación en el régimen
    if (!regimen.gratificacion || typeof regimen.gratificacion !== 'object') {
      console.warn("calcularGratificacion: régimen debe tener configuración de gratificación válida", {
        gratificacion: regimen.gratificacion
      });
      return 0;
    }

    console.log("calcularGratificacion: parámetros validados correctamente", {
      empleado: empleado.nombres + ' ' + empleado.apellidos,
      sueldo_base: sueldoBaseNum,
      regimen: regimen.nombre || regimen.codigo
    });

    // Verificar si aplica gratificación según régimen (e.g., MYPE no aplica)
    if (regimen.gratificacion.aplica === false || regimen.codigo === "MYPE_MICRO") {
      console.log("calcularGratificacion: gratificación no aplica para este régimen", {
        aplica: regimen.gratificacion.aplica,
        codigo: regimen.codigo
      });
      return 0;
    }

    // Usar fecha efectiva (reingreso si existe, sino ingreso)
    const fechaEfectiva = empleado.fecha_reingreso || empleado.fecha_ingreso;
    console.log("calcularGratificacion: fecha efectiva original:", fechaEfectiva, "tipo:", typeof fechaEfectiva);

    // Convertir fecha si es un Timestamp de Firestore
    let fechaEfectivaConvertida = fechaEfectiva;
    if (fechaEfectiva && typeof fechaEfectiva === 'object' && fechaEfectiva.toDate) {
      fechaEfectivaConvertida = fechaEfectiva.toDate();
      console.log("calcularGratificacion: convertido de Timestamp a Date:", fechaEfectivaConvertida);
    } else if (!(fechaEfectiva instanceof Date)) {
      fechaEfectivaConvertida = new Date(fechaEfectiva);
      console.log("calcularGratificacion: convertido de string a Date:", fechaEfectivaConvertida);
    }

    console.log("calcularGratificacion: fecha efectiva final:", fechaEfectivaConvertida, "es Date:", fechaEfectivaConvertida instanceof Date);

    // Obtener días no trabajados por faltas y permisos sin goce
    const periodoConsulta = periodo.fechaInicio.substring(0, 7); // YYYY-MM
    const { diasFaltas, diasPermisosSinGoce } = await obtenerDiasNoTrabajados(empleado.id || empleado.empleado_id, periodoConsulta);
    console.log("calcularGratificacion: días no trabajados obtenidos:", { diasFaltas, diasPermisosSinGoce });

    // Calcular tiempo computable
    const mesesComputables = calcularMesesComputables(fechaEfectivaConvertida, periodo, diasFaltas, diasPermisosSinGoce);
    console.log("calcularGratificacion: meses computables calculados:", mesesComputables);
    if (mesesComputables === 0) {
      console.log("calcularGratificacion: meses computables es 0, retornando 0");
      return 0;
    }

    // Base de cálculo: sueldo base (validar mínimo)
    let montoBase = Math.max(empleado.sueldo_base, parametros.rmv || 1130); // RMV mínimo
    console.log("calcularGratificacion: monto base inicial:", montoBase);

    // Agregar asignación familiar si corresponde (no en MYPE)
    if (
      regimen.asignacion_familiar.aplica &&
      empleado.asignacion_familiar &&
      regimen.codigo !== "MYPE_MICRO"
    ) {
      const asignacion = parametros.rmv * (regimen.asignacion_familiar.porcentaje_rmv / 100);
      montoBase += asignacion;
      console.log("calcularGratificacion: agregando asignación familiar:", asignacion, "total base:", montoBase);
    }

    // Agregar bonos remunerativos si el régimen los incluye en gratificación
    if (regimen.gratificacion.incluye_bonos_remunerativos) {
      const bonosRemunerativos = calcularBonosRemunerativos(empleado, historial, bonosMap);
      montoBase += bonosRemunerativos;
      console.log("calcularGratificacion: agregando bonos remunerativos:", bonosRemunerativos, "total base:", montoBase);
    }

    // Aplicar factor según régimen
    const factor = regimen.gratificacion.factor || 0;
    const montoGratificacion = montoBase * factor;
    console.log("calcularGratificacion: factor aplicado:", factor, "monto gratificación antes proporcional:", montoGratificacion);

    // Proporcional a meses trabajados
    const gratificacionFinal = (montoGratificacion * mesesComputables) / 6;
    console.log("calcularGratificacion: gratificación final calculada:", gratificacionFinal);
    return gratificacionFinal;
  } catch (error) {
    console.error("Error al calcular gratificación:", error);
    return 0;
  }
};

/**
 * Calcula la CTS según régimen laboral (incluyendo bonos remunerativos si aplican)
 * @param {Object} empleado - Datos del empleado
 * @param {Object} regimen - Configuración del régimen laboral
 * @param {Object} periodo - Periodo de cálculo (fechaInicio, fechaFin)
 * @param {Object} parametros - Parámetros generales de planilla
 * @param {number} ultimaGratificacion - Monto de última gratificación
 * @param {Array} historial - Opcional. Historial para validar regularidad
 * @param {Object} bonosMap - Opcional. Mapa de definiciones de bonos
 * @returns {Promise<number>} - Monto de CTS
 */
export const calcularCTS = async (
  empleado,
  regimen,
  periodo,
  parametros,
  ultimaGratificacion = 0,
  historial = [],
  bonosMap = {}
) => {
  console.log("🔍 calcularCTS - Iniciando cálculo", {
    empleado: {
      nombres: empleado?.nombres,
      apellidos: empleado?.apellidos,
      sueldo_base: empleado?.sueldo_base,
      fecha_ingreso: empleado?.fecha_ingreso,
      regimen_pensionario: empleado?.regimen_pensionario
    },
    regimen: {
      codigo: regimen?.codigo,
      cts: regimen?.cts,
      gratificacion: regimen?.gratificacion,
      asignacion_familiar: regimen?.asignacion_familiar
    },
    periodo,
    parametros,
    ultimaGratificacion
  });

  try {
    // Validaciones edge - mejorar validación de sueldo_base
    const sueldoBase = empleado?.sueldo_base;
    const sueldoBaseNum = typeof sueldoBase === 'string' ? parseFloat(sueldoBase) : Number(sueldoBase);

    if (!empleado || isNaN(sueldoBaseNum) || sueldoBaseNum <= 0) {
      console.warn("calcularCTS: empleado o sueldo_base inválido", {
        empleado: !!empleado,
        sueldo_base: sueldoBase,
        sueldo_base_tipo: typeof sueldoBase,
        sueldo_base_num: sueldoBaseNum
      });
      return 0;
    }

    // Actualizar el empleado con el sueldo_base convertido
    empleado.sueldo_base = sueldoBaseNum;
    if (!regimen || !regimen.cts) {
      console.warn("calcularCTS: régimen o configuración de CTS inválida");
      return 0;
    }

    // --- INICIO DE LA CORRECCIÓN ---
    // 1. Se ajusta la validación de 'aplica' para que solo retorne 0 si es explícitamente 'false'.
    if (regimen.cts.aplica === false || regimen.codigo === "MYPE_MICRO") {
      console.log("🔍 calcularCTS - CTS no aplica para este régimen");
      return 0;
    }

    // 2. Se elimina la validación del mes, ya que es redundante y causaba un error.
    // La lógica de qué mes se calcula ya está en la función que llama a esta.
    // --- FIN DE LA CORRECCIÓN ---

    // Usar fecha efectiva (reingreso si existe, sino ingreso)
    let fechaEfectiva = empleado.fecha_reingreso || empleado.fecha_ingreso;

    // Convertir fecha si es un Timestamp de Firestore
    if (fechaEfectiva && typeof fechaEfectiva === 'object' && fechaEfectiva.toDate) {
      fechaEfectiva = fechaEfectiva.toDate();
    } else if (!(fechaEfectiva instanceof Date)) {
      fechaEfectiva = new Date(fechaEfectiva);
    }

    // Obtener días no trabajados por faltas y permisos sin goce
    const periodoConsulta = periodo.fechaInicio.substring(0, 7); // YYYY-MM
    const { diasFaltas, diasPermisosSinGoce } = await obtenerDiasNoTrabajados(empleado.id || empleado.empleado_id, periodoConsulta);
    console.log("🔍 calcularCTS - días no trabajados obtenidos:", { diasFaltas, diasPermisosSinGoce });

    // Calcular tiempo computable
    const mesesComputables = calcularMesesComputables(fechaEfectiva, periodo, diasFaltas, diasPermisosSinGoce);
    console.log("🔍 calcularCTS - Meses computables calculados:", mesesComputables);
    if (mesesComputables === 0) {
      console.log("🔍 calcularCTS - No hay meses computables, retornando 0");
      return 0;
    }

    // Base de cálculo: sueldo base (validar mínimo)
    let montoBase = Math.max(empleado.sueldo_base, parametros.rmv || 1130);
    console.log("🔍 calcularCTS - Monto base inicial:", montoBase);

    // Agregar asignación familiar si corresponde (no en MYPE)
    if (
      regimen.asignacion_familiar.aplica &&
      empleado.asignacion_familiar &&
      regimen.codigo !== "MYPE_MICRO"
    ) {
      const asignacion = parametros.rmv * (regimen.asignacion_familiar.porcentaje_rmv / 100);
      montoBase += asignacion;
      console.log("🔍 calcularCTS - Agregando asignación familiar:", asignacion, "Total base:", montoBase);
    }

    // Agregar bonos remunerativos si el régimen los incluye en CTS
    if (regimen.cts.incluye_bonos_remunerativos) {
      const bonosRemunerativos = calcularBonosRemunerativos(empleado, historial, bonosMap);
      montoBase += bonosRemunerativos;
      console.log("🔍 calcularCTS - Agregando bonos remunerativos:", bonosRemunerativos, "Total base:", montoBase);
    }

    // Agregar 1/6 de última gratificación si corresponde
    if (
      regimen.gratificacion.aplica !== false && // Se ajusta también esta validación por consistencia
      ultimaGratificacion > 0 &&
      regimen.codigo !== "MYPE_MICRO" &&
      regimen.codigo !== "MYPE_PEQUENA" &&
      regimen.codigo !== "PEQUENA_EMPRESA" // Por si acaso
    ) {
      const gratificacionParte = ultimaGratificacion / 6;
      montoBase += gratificacionParte;
      console.log("🔍 calcularCTS - Agregando 1/6 gratificación:", gratificacionParte, "Total base:", montoBase);
    }

    // Aplicar factor según régimen
    const factor = regimen.cts.factor || 0;
    const montoCTSMensual = (montoBase * factor) / 12;
    console.log("🔍 calcularCTS - Factor CTS:", factor, "Monto CTS mensual:", montoCTSMensual);

    // Proporcional a meses trabajados
    const ctsFinal = montoCTSMensual * mesesComputables;
    console.log("🔍 calcularCTS - CTS final calculado:", ctsFinal);

    return ctsFinal;
  } catch (error) {
    console.error("Error al calcular CTS:", error);
    return 0;
  }
};

/**
 * Calcula vacaciones según régimen laboral
 * @param {Object} empleado - Datos del empleado
 * @param {Object} regimen - Configuración del régimen laboral
 * @param {Object} periodo - Periodo de cálculo
 * @returns {Object} - Información de vacaciones (dias, monto)
 */
export const calcularVacaciones = (empleado, regimen, periodo) => {
  try {
    // Validaciones edge
    if (!empleado || !empleado.fecha_ingreso) {
      console.warn("calcularVacaciones: empleado o fecha_ingreso inválida");
      return {
        dias: 0,
        monto: 0,
        corresponde: false,
        mensaje: "Datos inválidos",
      };
    }

    // Usar fecha efectiva (reingreso si existe, sino ingreso)
    const fechaEfectiva = empleado.fecha_reingreso || empleado.fecha_ingreso;

    // Convertir fecha si es un Timestamp de Firestore
    let fechaIngreso;
    if (fechaEfectiva && typeof fechaEfectiva === 'object' && fechaEfectiva.toDate) {
      fechaIngreso = fechaEfectiva.toDate();
    } else if (fechaEfectiva instanceof Date) {
      fechaIngreso = fechaEfectiva;
    } else {
      fechaIngreso = new Date(fechaEfectiva);
    }

    const fechaAniversario = addMonths(fechaIngreso, 12);
    const fechaFin = new Date(periodo.fechaFin);

    // Si no cumplió un año, no corresponde
    if (fechaFin < fechaAniversario) {
      return {
        dias: 0,
        monto: 0,
        corresponde: false,
        mensaje: "No cumple el año de servicios",
      };
    }

    // Verificar si el periodo incluye la fecha de aniversario laboral
    const fechaIni = new Date(periodo.fechaInicio);
    const esAniversario = isWithinInterval(fechaAniversario, {
      start: fechaIni,
      end: fechaFin,
    });

    if (!esAniversario) {
      return {
        dias: 0,
        monto: 0,
        corresponde: false,
        mensaje: "No es mes de aniversario laboral",
      };
    }

    // Días según régimen (MYPE tiene 15 días)
    let diasVacaciones =
      regimen.vacaciones.dias ||
      (regimen.codigo === "MYPE_MICRO" || regimen.codigo === "MYPE_PEQUENA"
        ? 15
        : 30);

    // Cálculo del monto (validar sueldo mínimo)
    const sueldoBase = Math.max(empleado.sueldo_base || 0, 1130); // RMV mínimo
    const montoVacaciones = (sueldoBase / 30) * diasVacaciones;

    return {
      dias: diasVacaciones,
      monto: montoVacaciones,
      corresponde: true,
      mensaje: `Corresponden ${diasVacaciones} días de vacaciones`,
    };
  } catch (error) {
    console.error("Error al calcular vacaciones:", error);
    return {
      dias: 0,
      monto: 0,
      corresponde: false,
      mensaje: "Error en el cálculo",
    };
  }
};

/**
 * Calcula el aporte de salud (ESSALUD o SIS) según régimen y configuración
 * @param {number} remuneracion - Remuneración afecta (incluye sueldo + bonos remunerativos)
 * @param {Object} regimen - Configuración del régimen
 * @param {Object} config - Configuración global de la empresa
 * @returns {number} - Monto de aporte
 */
export const calcularEssalud = (remuneracion, regimen, config = {}) => {
  console.log("calcularEssalud llamado con:", { remuneracion, regimen, tipo_seguro_micro: config?.tipo_seguro_micro });
  try {
    // Validaciones edge
    if (!remuneracion || remuneracion <= 0) {
      console.warn("calcularEssalud: remuneración inválida o cero");
      return 0;
    }

    // Caso Especial: SIS para Microempresa configurado a nivel de empresa
    if (regimen?.codigo === "MYPE_MICRO" && config?.tipo_seguro_micro === "SIS") {
      const costoSIS = Number(config.costo_sis) || 15.00;
      console.log("calcularEssalud: aplicando monto fijo SIS:", costoSIS);
      return costoSIS;
    }

    // Porcentaje según régimen (Generalmente 9%)
    let porcentaje = 9; 

    if (regimen && regimen.essalud && regimen.essalud.porcentaje) {
      porcentaje = regimen.essalud.porcentaje;
    }

    // Calcular directamente sobre la remuneración
    return Number((remuneracion * (porcentaje / 100)).toFixed(2));
  } catch (error) {
    console.error("Error al calcular aporte de salud:", error);
    return 0;
  }
};

/**
 * Calcula la renta de quinta categoría (el ingresoMensual debe incluir bonos remunerativos)
 * @param {number} ingresoMensual - Ingreso mensual actual (incluye sueldo + bonos remunerativos)
 * @param {number} mesesFaltantes - Meses restantes en el año
 * @param {number} gratificacionesProyectadas - Gratificaciones proyectadas anuales
 * @param {number} retencionesYaEfectuadas - Total de retenciones reales practicadas en meses anteriores
 * @param {number} ingresosAcumulados - Total de ingresos afectos acumulados en meses anteriores
 * @param {Object} config - Configuración general
 * @returns {number} - Monto de retención mensual
 */
export const calcularRenta5ta = (
  ingresoMensual,
  mesesFaltantes = 12,
  gratificacionesProyectadas = 0,
  retencionesYaEfectuadas = 0,
  ingresosAcumulados = 0,
  config = {},
  ingresoAnualManual = null // Nuevo parámetro opcional
) => {
  try {
    // Validaciones exhaustivas de parámetros de entrada
    if (typeof ingresoMensual !== 'number' || isNaN(ingresoMensual) || ingresoMensual < 0) {
      console.warn("calcularRenta5ta: ingresoMensual debe ser un número positivo", {
        ingresoMensual,
        tipo: typeof ingresoMensual
      });
      return 0;
    }

    if (typeof mesesFaltantes !== 'number' || isNaN(mesesFaltantes) || mesesFaltantes < 1 || mesesFaltantes > 12) {
      console.warn("calcularRenta5ta: mesesFaltantes debe ser un número entre 1 y 12", {
        mesesFaltantes,
        tipo: typeof mesesFaltantes
      });
      return 0;
    }

    if (typeof gratificacionesProyectadas !== 'number' || isNaN(gratificacionesProyectadas) || gratificacionesProyectadas < 0) {
      console.warn("calcularRenta5ta: gratificacionesProyectadas debe ser un número no negativo", {
        gratificacionesProyectadas,
        tipo: typeof gratificacionesProyectadas
      });
      return 0;
    }

    if (typeof retencionesYaEfectuadas !== 'number' || isNaN(retencionesYaEfectuadas) || retencionesYaEfectuadas < 0) {
      console.warn("calcularRenta5ta: retencionesYaEfectuadas debe ser un número no negativo", {
        retencionesYaEfectuadas,
        tipo: typeof retencionesYaEfectuadas
      });
      return 0;
    }

    // Validar configuración
    if (!config || typeof config !== 'object') {
      console.error("calcularRenta5ta: config debe ser un objeto válido");
      return 0;
    }

    // UIT para el año actual (obtener de config, sin fallback hardcoded)
    const UIT = config.uit;
    if (typeof UIT !== 'number' || isNaN(UIT) || UIT <= 0) {
      console.error("calcularRenta5ta: UIT no definido o inválido en config", {
        uit: UIT,
        config: config
      });
      return 0;
    }

    console.log("calcularRenta5ta: parámetros validados correctamente", {
      ingresoMensual,
      mesesFaltantes,
      gratificacionesProyectadas,
      retencionesYaEfectuadas,
      ingresosAcumulados,
      uit: UIT,
      ingresoAnualManual
    });

    // Cálculo de la proyección anual (incluyendo gratificaciones proyectadas)
    let ingresoAnualTotal;

    if (ingresoAnualManual !== null && typeof ingresoAnualManual === 'number') {
      // Uso de proyección externa manual
      ingresoAnualTotal = ingresoAnualManual;
    } else {
      // Proyección estándar interna
      const mesesTranscurridos = 12 - mesesFaltantes;
      const ingresoPercibido = ingresosAcumulados; // Usar ingresos reales acumulados
      const ingresoProyectado = ingresoMensual * mesesFaltantes;
      ingresoAnualTotal = ingresoPercibido + ingresoProyectado + gratificacionesProyectadas;
    }

    // Deducciones (7 UIT)
    const deduccion = 7 * UIT;
    const rentaNeta = ingresoAnualTotal - deduccion;

    // Si no hay renta neta imponible, no hay impuesto
    if (rentaNeta <= 0) return 0;

    // Definición de las escalas y tasas 2026 (ACTUALIZADAS)
    const escalas = [
      { hasta: 5 * UIT, tasa: 0.08 },    // Hasta 5 UIT (27,500): 8%
      { hasta: 20 * UIT, tasa: 0.14 },   // Hasta 20 UIT (110,000): 14%
      { hasta: 35 * UIT, tasa: 0.17 },   // Hasta 35 UIT (192,500): 17%
      { hasta: 45 * UIT, tasa: 0.20 },   // Hasta 45 UIT (247,500): 20%
      { hasta: Infinity, tasa: 0.30 },   // Más de 45 UIT: 30%
    ];

    // Cálculo del impuesto por escalas PROGRESIVAS
    let impuestoAnual = 0;
    let baseRestante = rentaNeta;
    let tramoAnterior = 0;

    for (const escala of escalas) {
      const tramoHasta = escala.hasta;
      const tramoMonto = tramoHasta - tramoAnterior;
      const montoEnTramo = Math.min(baseRestante, tramoMonto);

      if (montoEnTramo > 0) {
        impuestoAnual += montoEnTramo * escala.tasa;
        baseRestante -= montoEnTramo;
        tramoAnterior = tramoHasta;
      }

      if (baseRestante <= 0 || tramoHasta === Infinity) break;
    }

    // Usar retenciones reales ya efectuadas
    const saldoPorRetener = impuestoAnual - retencionesYaEfectuadas;

    // Retención mensual para los meses restantes (INCLUYE MES ACTUAL)
    const mesesParaDistribuir = mesesFaltantes; // Ya viene ajustado desde planillaHelpers
    const retencionMensual = mesesParaDistribuir > 0 ? saldoPorRetener / mesesParaDistribuir : 0;

    // Redondeo a 2 decimales
    return Number(retencionMensual.toFixed(2));
  } catch (error) {
    console.error("Error en calcularRenta5ta:", error);
    return 0; // Fallback seguro
  }
};

/**
 * Calcula descuentos por faltas y tardanzas
 * @param {Object} empleado - Datos del empleado
 * @param {Object} faltas - Objeto con faltas injustificadas
 * @param {Object} permisos - Objeto con permisos sin goce
 * @param {Object} configuracion - Configuración del régimen
 * @returns {Object} - Detalles de descuentos
 */
export const calcularDescuentosPorFaltas = (
  empleado,
  faltas,
  permisos,
  configuracion,
  jornada = {},
  regimenLaboral = 'GENERAL'
) => {
  try {
    // Validaciones de entrada - mejorar validación de sueldo_base
    const sueldoBase = empleado?.sueldo_base;
    const sueldoBaseNum = typeof sueldoBase === 'string' ? parseFloat(sueldoBase) : Number(sueldoBase);

    if (!empleado || isNaN(sueldoBaseNum) || sueldoBaseNum <= 0) {
      console.warn(
        "calcularDescuentosPorFaltas: empleado o sueldo_base inválido", {
        empleado: !!empleado,
        sueldo_base: sueldoBase,
        sueldo_base_tipo: typeof sueldoBase,
        sueldo_base_num: sueldoBaseNum
      }
      );
      return { dias_descontados: 0, monto_descuento: 0, detalle: {} };
    }

    // Actualizar el empleado con el sueldo_base convertido
    empleado.sueldo_base = sueldoBaseNum;
    if (!faltas || typeof faltas !== "object") {
      console.warn("calcularDescuentosPorFaltas: faltas inválido");
      faltas = { injustificadas: 0 };
    }
    if (!permisos || typeof permisos !== "object") {
      console.warn("calcularDescuentosPorFaltas: permisos inválido");
      permisos = { sin_goce: 0 };
    }

    // H013-E: La asignación familiar NO integra la base para descuentos por inasistencia.
    // Fundamento: Ley 25129, D.S. 035-90-TR art. 3, Informe 385-2011-MTPE/4/8,
    // Informe 028-2012-MTPE/2/14, Res. 402-2021-SUNAFIL/TFL y Res. 305-2022-SUNAFIL/TFL.
    // La AF se paga íntegramente y no se reduce proporcionalmente por días no laborados.
    const baseParaDescuento = sueldoBaseNum;
    const sueldoDiario = baseParaDescuento / 30;

    if (sueldoDiario <= 0) {
      console.error("calcularDescuentosPorFaltas: sueldoDiario inválido");
      return { dias_descontados: 0, monto_descuento: 0, detalle: {} };
    }

    const faltasInjustificadas = Number(faltas.injustificadas || 0);
    const permisosSinGoce = Number(permisos.sin_goce || 0);
    const diasTrabajoJornada = Math.max(1, Number(jornada.diasTrabajo || 6));
    const diasDescansoJornada = Math.max(0, Number(jornada.diasDescanso || 1));

    // Falta injustificada:
    // H013-E2: Solo sueldoDiario (descuento directo por día no trabajado)
    const descuentoFaltaPorDia = sueldoDiario;
    const montoDescuentoFaltas = descuentoFaltaPorDia * faltasInjustificadas;

    // H013-E2: DSO — Descuento proporcional del Descanso Semanal Obligatorio
    // D.S. 012-92-TR, Art. 2: "un treintavo del valor día por cada día de inasistencia"
    // valorDia / 30 = un treintavo del valor día
    const DSO_POR_INASISTENCIA = sueldoDiario / 30;
    const montoDSO = DSO_POR_INASISTENCIA * faltasInjustificadas;

    // Permiso sin goce:
    // descuento día + proporcional de todos los descansos de la jornada
    const descuentoPermisoPorDia = sueldoDiario + ((sueldoDiario * diasDescansoJornada) / diasTrabajoJornada);
    const montoDescuentoPermisosSinGoce = descuentoPermisoPorDia * permisosSinGoce;

    const diasDescuento = Math.max(0, faltasInjustificadas + permisosSinGoce);
    const montoDescuentoTotal = montoDescuentoFaltas + montoDSO + montoDescuentoPermisosSinGoce;

    return {
      dias_descontados: diasDescuento,
      monto_descuento: Number(montoDescuentoFaltas.toFixed(2)),
      monto_dso: Number(montoDSO.toFixed(2)),
      monto_descuento_permisos_sin_goce: Number(montoDescuentoPermisosSinGoce.toFixed(2)),
      monto_descuento_total_ausencias: Number(montoDescuentoTotal.toFixed(2)),
      detalle: {
        faltas_injustificadas: faltasInjustificadas,
        permisos_sin_goce: permisosSinGoce,
        permisos_con_goce: permisos.con_goce || 0,
        jornada_dias_trabajo: diasTrabajoJornada,
        jornada_dias_descanso: diasDescansoJornada,
        sueldo_diario: Number(sueldoDiario.toFixed(4)),
        descuento_falta_por_dia: Number(descuentoFaltaPorDia.toFixed(4)),
        descuento_permiso_por_dia: Number(descuentoPermisoPorDia.toFixed(4)),
      },
    };
  } catch (error) {
    console.error("Error en calcularDescuentosPorFaltas:", error);
    return { dias_descontados: 0, monto_descuento: 0, detalle: {} };
  }
};

/**
 * Calcula descuentos por tardanzas
 * @param {Object} empleado - Datos del empleado
 * @param {Array} tardanzas - Array de tardanzas
 * @param {Object} configuracion - Configuración del régimen
 * @returns {Object} - Detalles de descuentos por tardanzas
 */
export const calcularDescuentosPorTardanzas = (
  empleado,
  tardanzas,
  configuracion
) => {
  try {
    // Validaciones de entrada - mejorar validación de sueldo_base
    const sueldoBase = empleado?.sueldo_base;
    const sueldoBaseNum = typeof sueldoBase === 'string' ? parseFloat(sueldoBase) : Number(sueldoBase);

    if (!empleado || isNaN(sueldoBaseNum) || sueldoBaseNum <= 0) {
      console.warn("calcularDescuentosPorTardanzas: empleado o sueldo_base inválido", {
        empleado: !!empleado,
        sueldo_base: sueldoBase,
        sueldo_base_tipo: typeof sueldoBase,
        sueldo_base_num: sueldoBaseNum
      });
      return {
        dias_con_tardanza: 0,
        minutos_totales: 0,
        monto_descuento: 0,
        detalle: [],
      };
    }

    // Actualizar el empleado con el sueldo_base convertido
    empleado.sueldo_base = sueldoBaseNum;

    // Validar que tardanzas sea un array
    if (!Array.isArray(tardanzas)) {
      console.warn("calcularDescuentosPorTardanzas: tardanzas no es un array válido", tardanzas);
      tardanzas = [];
    }

    // Configuración de tolerancia (por defecto 10 minutos)
    const minutosTolerancia = configuracion?.tolerancia_minutos || 10;

    // H013-C: Valor día/hora/minuto según Informe 513-2023-MTPE/2/14.1
    // Remuneración mensual → divisor 30 días calendario (no 25 días laborables)
    const DIAS_BASE_MENSUAL = 30;
    const HORAS_JORNADA = 8; // Jornada estándar 8 horas (no hay configuración de jornada variable para tardanzas)
    const MINUTOS_POR_HORA = 60;

    const valorDia = empleado.sueldo_base / DIAS_BASE_MENSUAL;
    const valorHora = valorDia / HORAS_JORNADA;
    const valorMinuto = valorHora / MINUTOS_POR_HORA;

    let minutosDescuento = 0;
    let diasTardanza = 0;

    tardanzas.forEach((tardanza) => {
      const minutosExceso = Math.max(0, tardanza.minutos - minutosTolerancia);
      minutosDescuento += minutosExceso;
      if (minutosExceso > 0) diasTardanza++;
    });

    const montoDescuento = valorMinuto * minutosDescuento;

    return {
      dias_con_tardanza: diasTardanza,
      minutos_totales: minutosDescuento,
      monto_descuento: Number(montoDescuento.toFixed(2)),
      detalle: tardanzas,
    };
  } catch (error) {
    console.error("Error al calcular descuentos por tardanzas:", error);
    return {
      dias_con_tardanza: 0,
      minutos_totales: 0,
      monto_descuento: 0,
      detalle: [],
    };
  }
};

/**
 * Calcula la indemnización por despido según régimen
 * @param {Object} empleado - Datos del empleado
 * @param {Object} regimen - Configuración del régimen
 * @param {Date} fechaDespido - Fecha de despido
 * @returns {Object} - Datos de indemnización
 */
export const calcularIndemnizacionDespido = (
  empleado,
  regimen,
  fechaDespido
) => {
  try {
    // Convertir fecha de ingreso si es un Timestamp de Firestore
    let fechaIngreso;
    if (empleado.fecha_ingreso && typeof empleado.fecha_ingreso === 'object' && empleado.fecha_ingreso.toDate) {
      fechaIngreso = empleado.fecha_ingreso.toDate();
    } else if (empleado.fecha_ingreso instanceof Date) {
      fechaIngreso = empleado.fecha_ingreso;
    } else {
      fechaIngreso = new Date(empleado.fecha_ingreso);
    }

    // Convertir fecha de despido si es un Timestamp de Firestore
    let fechaDespidoObj;
    if (fechaDespido && typeof fechaDespido === 'object' && fechaDespido.toDate) {
      fechaDespidoObj = fechaDespido.toDate();
    } else if (fechaDespido instanceof Date) {
      fechaDespidoObj = fechaDespido;
    } else {
      fechaDespidoObj = new Date(fechaDespido);
    }

    // Calcular años de servicio (fracción superior a 3 meses cuenta como año completo)
    const mesesServicio = differenceInMonths(fechaDespidoObj, fechaIngreso);
    let añosServicio = Math.floor(mesesServicio / 12);
    const mesesAdicionales = mesesServicio % 12;

    // Si hay más de 3 meses adicionales, se considera un año más
    if (mesesAdicionales > 3) {
      añosServicio += 1;
    }

    // Si no hay años de servicio computables, no corresponde
    if (añosServicio === 0) {
      return {
        años: 0,
        monto: 0,
        detalle: "Menos de 3 meses de servicio",
      };
    }

    let montoIndemnizacion = 0;
    let detalle = "";

    switch (regimen.codigo) {
      case "GENERAL":
        // 1.5 sueldos por año, tope 12 años
        const añosComputables = Math.min(
          añosServicio,
          regimen.indemnizacion.tope_anios || 12
        );
        montoIndemnizacion =
          empleado.sueldo_base *
          regimen.indemnizacion.factor_por_anio *
          añosComputables;
        detalle = `${añosComputables} años x ${regimen.indemnizacion.factor_por_anio} sueldos`;
        break;

      case "MYPE_MICRO":
      case "MYPE_PEQUENA":
        // Factor diario * días tope
        const diasPorAnio =
          (empleado.sueldo_base / 30) *
          regimen.indemnizacion.factor_por_anio *
          30;
        const diasTotales = Math.min(
          diasPorAnio * añosServicio,
          regimen.indemnizacion.tope_dias
        );
        montoIndemnizacion = diasTotales * (empleado.sueldo_base / 30);
        detalle = `${diasTotales} días de remuneración (tope: ${regimen.indemnizacion.tope_dias})`;
        break;

      case "AGRARIO":
        // 45 días por año
        montoIndemnizacion = empleado.sueldo_base * 1.5 * añosServicio;
        detalle = `${añosServicio} años x 45 días por año`;
        break;

      default:
        detalle = "Régimen sin indemnización definida";
    }

    return {
      años: añosServicio,
      monto: montoIndemnizacion,
      detalle,
    };
  } catch (error) {
    console.error("Error al calcular indemnización:", error);
    return { años: 0, monto: 0, detalle: "Error en cálculo" };
  }
};

/**
 * Calcula beneficios de construcción civil
 * @param {Object} empleado - Datos del empleado
 * @param {Object} regimen - Configuración del régimen
 * @param {Object} condiciones - Condiciones especiales (altura, agua, etc)
 * @returns {Object} - Bonificaciones calculadas
 */
export const calcularBonificacionesConstruccionCivil = (
  empleado,
  regimen,
  condiciones
) => {
  try {
    // Si no es régimen de construcción civil, no aplica
    if (regimen.codigo !== "CONSTRUCCION_CIVIL") {
      return {};
    }

    const { categoria } = empleado;
    const { salarios, bonificaciones } = regimen;
    const resultado = {};

    // Jornal según categoría
    const jornal = empleado.jornal_base || empleado.sueldo_base / 30;

    // Factor según categoría
    let factor = 1;
    if (categoria === "OFICIAL") {
      factor = salarios.oficial.factor_jornal;
    } else if (categoria === "PEON") {
      factor = salarios.peon.factor_jornal;
    }

    resultado.jornal_categoria = jornal * factor;

    // Bonificaciones
    if (condiciones.altura && bonificaciones.altura) {
      resultado.bonif_altura =
        resultado.jornal_categoria * (bonificaciones.altura.porcentaje / 100);
    }

    if (condiciones.agua && bonificaciones.agua) {
      resultado.bonif_agua =
        resultado.jornal_categoria * (bonificaciones.agua.porcentaje / 100);
    }

    if (condiciones.toxicidad && bonificaciones.toxicidad) {
      resultado.bonif_toxicidad =
        resultado.jornal_categoria *
        (bonificaciones.toxicidad.porcentaje / 100);
    }

    if (condiciones.altitud && bonificaciones.altitud) {
      if (condiciones.altitud >= 4000) {
        resultado.bonif_altitud =
          resultado.jornal_categoria *
          (bonificaciones.altitud.mayor_4000msnm / 100);
      } else if (condiciones.altitud >= 3000) {
        resultado.bonif_altitud =
          resultado.jornal_categoria *
          (bonificaciones.altitud.mayor_3000msnm / 100);
      }
    }

    // Calcular total
    resultado.total_bonificaciones = Object.values(resultado)
      .filter(
        (val) => typeof val === "number" && val !== resultado.jornal_categoria
      )
      .reduce((sum, val) => sum + val, 0);

    return resultado;
  } catch (error) {
    console.error(
      "Error al calcular bonificaciones de construcción civil:",
      error
    );
    return {};
  }
};

export const calcularSistemaPensionario = (totalIngresos, empleado, config, fechaReferencia = null) => {
  try {
    console.log("🔍 calcularSistemaPensionario - Iniciando cálculo", {
      totalIngresos,
      empleado: {
        regimen_pensionario: empleado?.regimen_pensionario,
        afp: empleado?.afp
      },
      config: {
        porcentaje_onp: config?.porcentaje_onp,
        afp_keys: Object.keys(config || {}).filter(key => key.startsWith('afp_'))
      }
    });

    if (!totalIngresos || totalIngresos <= 0) {
      console.warn("calcularSistemaPensionario: totalIngresos inválido");
      return { fondo: 0, comision: 0, seguro: 0, onp: 0, total: 0 };
    }
    if (!empleado || !empleado.regimen_pensionario) {
      console.warn(
        "calcularSistemaPensionario: empleado o regimen_pensionario inválido"
      );
      return { fondo: 0, comision: 0, seguro: 0, onp: 0, total: 0 };
    }

    let sistemaPension = {
      fondo: 0,
      comision: 0,
      seguro: 0,
      onp: 0,
      total: 0,
    };

    // Tope SBS para Seguro de Invalidez y Sobrevivencia (SIS) - 2026 aprox.
    const TOPE_SIS_SBS = config?.tope_seguro_sbs || 11200;

    if (empleado.regimen_pensionario === "ONP") {
      console.log("🔍 calcularSistemaPensionario - Procesando ONP", {
        porcentaje_onp: config?.porcentaje_onp,
        config_keys: Object.keys(config || {})
      });

      const porcentajeOnp = config?.porcentaje_onp || 0.13; // Default 13% si no está definido
      const descuentoOnp = Number((totalIngresos * porcentajeOnp).toFixed(2));

      console.log("🔍 calcularSistemaPensionario - ONP calculado", {
        porcentajeOnp,
        descuentoOnp,
        totalIngresos
      });

      sistemaPension = {
        fondo: 0,
        comision: 0,
        seguro: 0,
        onp: descuentoOnp,
        total: descuentoOnp,
      };
    } else if (empleado.regimen_pensionario === "AFP" && empleado.afp) {
      const afpKey = `afp_${empleado.afp.toLowerCase()}`;
      const afpConfig = config[afpKey];

      console.log("🔍 calcularSistemaPensionario - Procesando AFP", {
        afpKey,
        afpConfig: !!afpConfig,
        empleado_afp: empleado.afp
      });

      if (afpConfig) {
        const fondoAfp = Number((totalIngresos * afpConfig.fondo).toFixed(2));
        let comisionAfp = Number((totalIngresos * afpConfig.comision).toFixed(2));
        if (empleado.fecha_termino_comision_afp) {
          const fechaLimite = new Date(empleado.fecha_termino_comision_afp);
          const fechaComparacion = fechaReferencia ? new Date(fechaReferencia) : new Date();
          if (fechaComparacion < fechaLimite) {
            comisionAfp = 0; // Comisión 0% durante los primeros 10 años
          }
        }
        
        // CORRECCIÓN: Aplicar tope SBS para el Seguro de AFP (SIS)
        const baseSeguro = Math.min(totalIngresos, TOPE_SIS_SBS);
        const seguroAfp = Number((baseSeguro * afpConfig.seguro).toFixed(2));
        
        const totalDescuento = fondoAfp + seguroAfp + comisionAfp;

        sistemaPension = {
          fondo: fondoAfp,
          comision: comisionAfp,
          seguro: seguroAfp,
          onp: 0,
          total: totalDescuento,
          nombre_afp: empleado.afp,
        };
      } else {
        console.warn("🔍 calcularSistemaPensionario - Configuración AFP no encontrada", {
          afpKey,
          available_keys: Object.keys(config || {})
        });
      }
    } else {
      console.warn("🔍 calcularSistemaPensionario - Régimen no reconocido", {
        regimen: empleado.regimen_pensionario,
        afp: empleado.afp
      });
    }

    console.log("🔍 calcularSistemaPensionario - Resultado final", sistemaPension);
    return sistemaPension;
  } catch (error) {
    console.error("Error en calcularSistemaPensionario:", error);
    return { fondo: 0, comision: 0, seguro: 0, onp: 0, total: 0 };
  }
};

// Función para calcular EsSalud Vida
export const calcularEssaludVida = (empleado, config) => {
  try {
    if (!empleado) {
      console.warn("calcularEssaludVida: empleado inválido");
      return 0;
    }

    const essaludVida =
      empleado.essalud_vida === true ? Number(config.essalud_vida || 0) : 0;
    return essaludVida;
  } catch (error) {
    console.error("Error en calcularEssaludVida:", error);
    return 0;
  }
};

// Función para calcular SCTR
export const calcularSCTR = (totalIngresos, empleado, config) => {
  try {
    console.log("🔍 calcularSCTR - Iniciando cálculo", {
      totalIngresos,
      empleado_sctr: empleado?.sctr,
      config_sctr: config?.sctr
    });

    if (!totalIngresos || totalIngresos <= 0) {
      console.log("🔍 calcularSCTR - totalIngresos inválido, retornando 0");
      return { pension: 0, salud: 0, total: 0 };
    }
    if (!empleado || !empleado.sctr) {
      // SCTR es opcional, no mostrar advertencia si no está configurado
      console.log("🔍 calcularSCTR - Empleado sin SCTR configurado, retornando 0");
      return { pension: 0, salud: 0, total: 0 };
    }
    if (!config || !config.sctr) {
      console.warn("calcularSCTR: config.sctr inválido");
      return { pension: 0, salud: 0, total: 0 };
    }

    const riesgo = `riesgo_${empleado.sctr}`;
    console.log("🔍 calcularSCTR - Riesgo calculado:", riesgo);

    if (!config.sctr[riesgo]) {
      console.warn(`calcularSCTR: riesgo '${riesgo}' no encontrado en config`, {
        available_riesgos: Object.keys(config.sctr)
      });
      return { pension: 0, salud: 0, total: 0 };
    }

    const sctrConfig = config.sctr[riesgo];
    console.log("🔍 calcularSCTR - Configuración del riesgo:", sctrConfig);

    const sctrPension = Number(
      (totalIngresos * sctrConfig.pension).toFixed(2)
    );
    const sctrSalud = Number(
      (totalIngresos * sctrConfig.salud).toFixed(2)
    );
    const sctr = Number((sctrPension + sctrSalud).toFixed(2));

    console.log("🔍 calcularSCTR - Cálculos detallados", {
      totalIngresos,
      pension_porcentaje: sctrConfig.pension,
      salud_porcentaje: sctrConfig.salud,
      sctrPension,
      sctrSalud,
      sctr_total: sctr
    });

    return {
      pension: sctrPension,
      salud: sctrSalud,
      total: sctr
    };
  } catch (error) {
    console.error("Error en calcularSCTR:", error);
    return { pension: 0, salud: 0, total: 0 };
  }
};

// Función para calcular Vida Ley
export const calcularVidaLey = (empleado) => {
  try {
    if (!empleado) {
      console.warn("calcularVidaLey: empleado inválido");
      return 0;
    }

    const vidaLey = Number(empleado.vida_ley_importe || 0);
    return vidaLey;
  } catch (error) {
    console.error("Error en calcularVidaLey:", error);
    return 0;
  }
};

// Función para calcular provisión mensual de vacaciones
export const calcularProvisiónVacaciones = (sueldoBase, diasVacaciones = 30) => {
  try {
    if (!sueldoBase || sueldoBase <= 0) {
      console.warn("calcularProvisiónVacaciones: sueldoBase inválido", sueldoBase);
      return 0;
    }
    if (!diasVacaciones || diasVacaciones <= 0) {
      console.warn("calcularProvisiónVacaciones: diasVacaciones inválido", diasVacaciones);
      return 0;
    }

    // Provisión mensual = (sueldo base / 12) * (días de vacaciones / 30)
    const provisionMensual = (sueldoBase / 12) * (diasVacaciones / 30);
    return Number(provisionMensual.toFixed(2));
  } catch (error) {
    console.error("Error en calcularProvisiónVacaciones:", error);
    return 0;
  }
};

// Función para calcular provisión mensual de gratificaciones
export const calcularProvisiónGratificaciones = (baseGratificacion, numGratificaciones = 2, factor = 1) => {
  try {
    if (!baseGratificacion || baseGratificacion <= 0) {
      console.warn("calcularProvisiónGratificaciones: baseGratificacion inválida", baseGratificacion);
      return 0;
    }

    // Total gratificaciones anuales = base * factor * numGratificaciones
    const totalGratificaciones = baseGratificacion * factor * numGratificaciones;
    // Provisión mensual = total / 12
    const provisionMensual = totalGratificaciones / 12;
    return Number(provisionMensual.toFixed(2));
  } catch (error) {
    console.error("Error en calcularProvisiónGratificaciones:", error);
    return 0;
  }
};

// Función para calcular tiempo de servicio
const calcularTiempoServicio = (fechaIngreso, fechaCese) => {
  try {
    const diffTime = Math.abs(fechaCese - fechaIngreso);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const años = Math.floor(diffDays / 365.25);
    const mesesRestantes = Math.floor((diffDays % 365.25) / 30.44);
    const diasRestantes = Math.floor((diffDays % 365.25) % 30.44);

    return {
      años,
      meses: mesesRestantes,
      dias: diasRestantes,
      totalDias: diffDays,
      totalMeses: años * 12 + mesesRestantes,
      totalAños: años + (mesesRestantes / 12) + (diasRestantes / 365.25)
    };
  } catch (error) {
    console.error("Error calculando tiempo de servicio:", error);
    return { años: 0, meses: 0, dias: 0, totalDias: 0, totalMeses: 0, totalAños: 0 };
  }
};

// Función para obtener reglas por régimen laboral
const obtenerReglasPorRegimen = (codigoRegimen) => {
  const reglasBase = {
    GENERAL: {
      gratificacion: { factor: 1.0, divisor: 6, inafecto: true, aplica: true },
      cts: { factor: 1.0, divisor: 12, inafecto: true, aplica: true },
      vacaciones: { diasAnuales: 30, divisor: 30, inafecto: false, aplica: true },
      indemnizacion: {
        factorPorAnio: 1.5,
        topeAnios: 12,
        inafecto: true,
        aplica: true,
        detalle: "1.5 sueldos por año, máximo 12 años"
      }
    },
    MYPE_PEQUENA: {
      gratificacion: { factor: 0.5, divisor: 6, inafecto: true, aplica: true },
      cts: { factor: 0.5, divisor: 12, inafecto: true, aplica: true },
      vacaciones: { diasAnuales: 15, divisor: 15, inafecto: false, aplica: true },
      indemnizacion: {
        diasPorAnio: 20,
        topeDias: 120,
        inafecto: true,
        aplica: true,
        detalle: "20 días por año, máximo 120 días"
      }
    },
    MYPE_MICRO: {
      gratificacion: { factor: 0, divisor: 6, inafecto: true, aplica: false },
      cts: { factor: 0, divisor: 12, inafecto: true, aplica: false },
      vacaciones: { diasAnuales: 15, divisor: 15, inafecto: false, aplica: true },
      indemnizacion: {
        diasPorAnio: 10,
        topeDias: 90,
        inafecto: true,
        aplica: true,
        detalle: "10 días por año, máximo 90 días"
      }
    },
    AGRARIO: {
      gratificacion: { factor: 1.0, divisor: 6, inafecto: true, aplica: true },
      cts: { factor: 1.0, divisor: 12, inafecto: true, aplica: true },
      vacaciones: { diasAnuales: 30, divisor: 30, inafecto: false, aplica: true },
      indemnizacion: {
        factorPorAnio: 1.5,
        topeAnios: 12,
        inafecto: true,
        aplica: true,
        detalle: "45 días por año (1.5 sueldos)"
      }
    },
    CONSTRUCCION_CIVIL: {
      gratificacion: { factor: 1.0, divisor: 6, inafecto: true, aplica: true },
      cts: { factor: 1.0, divisor: 12, inafecto: true, aplica: true },
      vacaciones: { diasAnuales: 30, divisor: 30, inafecto: false, aplica: true },
      indemnizacion: {
        factorPorAnio: 1.5,
        topeAnios: 12,
        inafecto: true,
        aplica: true,
        detalle: "1.5 sueldos por año, máximo 12 años"
      }
    }
  };

  return reglasBase[codigoRegimen] || reglasBase.GENERAL;
};

// Función para calcular gratificación trunca
const calcularGratificacionTrunca = (RC, tiempoServicio, reglas, fechaCese, parametros, gratificacionPagada = false) => {
  try {
    console.log("🔍 calcularGratificacionTrunca - Parámetros recibidos:", {
      RC,
      reglas: reglas.gratificacion,
      fechaCese,
      gratificacionPagada
    });

    if (!reglas.gratificacion.aplica) {
      return { monto: 0, detalle: "Gratificación no aplica para este régimen" };
    }

    // Si la gratificación ya se pagó, no calcular trunca
    if (gratificacionPagada) {
      return { monto: 0, detalle: "Gratificación ya pagada en planilla regular" };
    }

    const mesCese = fechaCese.getMonth() + 1; // 1-12
    const diaCese = fechaCese.getDate();
    const ultimoDiaMes = new Date(fechaCese.getFullYear(), fechaCese.getMonth() + 1, 0).getDate();

    // Determinar el período de gratificación (Ene-Jun o Jul-Dic)
    const esPeriodoEneroJunio = mesCese <= 6;
    const mesInicioPeriodo = esPeriodoEneroJunio ? 1 : 7;

    // Calcular meses completos en el período
    let mesesCompletos = 0;

    for (let mes = mesInicioPeriodo; mes <= mesCese; mes++) {
      if (mes === mesCese) {
        // Para el mes de cese: incluir solo si es el último día del mes
        if (diaCese === ultimoDiaMes) {
          mesesCompletos++;
        }
        // Si no es el último día, no incluir este mes
      } else {
        // Meses anteriores al de cese: siempre incluir
        mesesCompletos++;
      }
    }

    console.log("🔍 calcularGratificacionTrunca - Meses completos:", {
      mesCese,
      diaCese,
      ultimoDiaMes,
      esPeriodoEneroJunio,
      mesInicioPeriodo,
      mesesCompletos
    });

    // Gratificación = (RC × factor / divisor) × meses completos laborados
    const montoBase = (RC * reglas.gratificacion.factor) / reglas.gratificacion.divisor;
    const monto = montoBase * mesesCompletos;

    console.log("🔍 calcularGratificacionTrunca - Cálculo base:", {
      RC,
      factor: reglas.gratificacion.factor,
      divisor: reglas.gratificacion.divisor,
      montoBase,
      mesesCompletos,
      montoSinBonificacion: monto
    });

    // Agregar bonificación extraordinaria (9%)
    const bonificacion = monto * 0.09;
    const montoTotal = monto + bonificacion;

    console.log("🔍 calcularGratificacionTrunca - Resultado final:", {
      monto,
      bonificacion,
      montoTotal
    });

    return {
      monto: montoTotal,
      detalle: `${mesesCompletos}/6 meses × S/ ${montoBase.toFixed(2)} + S/ ${bonificacion.toFixed(2)} bonificación (9%)`
    };
  } catch (error) {
    console.error("Error calculando gratificación trunca:", error);
    return { monto: 0, detalle: "Error en cálculo" };
  }
};

// Función para calcular CTS trunca
const calcularCTSTrunca = (RC, tiempoServicio, reglas, fechaCese, parametros) => {
  try {
    if (!reglas.cts.aplica) {
      return { monto: 0, detalle: "CTS no aplica para este régimen" };
    }

    // Base CTS = RC + (1/6 gratificación)
    // En Pequeña Empresa REMYPE no se incluye 1/6 de gratificación en la base de CTS
    const esMYPEPequena = reglas.codigo === "MYPE_PEQUENA" || reglas.codigo === "PEQUENA_EMPRESA";
    const gratificacionParte = esMYPEPequena ? 0 : (RC * reglas.gratificacion.factor) / reglas.gratificacion.divisor;
    const baseCTS = RC + gratificacionParte;

    // Nueva lógica de CTS para liquidación según periodos anuales
    // Si el cese es entre 1 de mayo y 31 de octubre → inicia el 1 de mayo del mismo año
    // Si el cese es entre 1 de noviembre y 30 de abril → inicia el 1 de noviembre del año anterior
    const mesCese = fechaCese.getMonth() + 1; // getMonth() devuelve 0-11
    const diaCese = fechaCese.getDate();
    const añoCese = fechaCese.getFullYear();

    let fechaInicioCTS;
    if ((mesCese > 4 && mesCese <= 10) || (mesCese === 5 && diaCese >= 1) || (mesCese === 10 && diaCese <= 31)) {
      // Cese entre 1 de mayo y 31 de octubre: contar desde 1 de mayo del mismo año
      fechaInicioCTS = new Date(añoCese, 4, 1); // Mayo es mes 4 (0-indexed), día 1
    } else {
      // Cese entre 1 de noviembre y 30 de abril: contar desde 1 de noviembre del año anterior
      fechaInicioCTS = new Date(añoCese - 1, 10, 1); // Noviembre es mes 10 (0-indexed), día 1
    }

    // Calcular tiempo desde fechaInicioCTS hasta fechaCese
    // Para CTS truncado, calcular meses completos entre las fechas
    const mesInicioCTS = fechaInicioCTS.getMonth();
    const mesCeseCalculado = fechaCese.getMonth();
    const diaInicioCTS = fechaInicioCTS.getDate();
    const diaCeseCalculado = fechaCese.getDate();

    let mesesAjustados = mesCeseCalculado - mesInicioCTS + 1;
    let diasAjustados = 0;

    // Si no ha completado el mes, ajustar
    if (diaCeseCalculado < diaInicioCTS) {
      mesesAjustados--;
      const ultimoDiaMesAnterior = new Date(fechaCese.getFullYear(), fechaCese.getMonth(), 0).getDate();
      diasAjustados = ultimoDiaMesAnterior - diaInicioCTS + diaCeseCalculado + 1;
    }

    // CTS = (Base CTS / 12) × meses + (Base CTS / 360) × días
    const montoMensual = (baseCTS * reglas.cts.factor) / reglas.cts.divisor;
    const montoPorMeses = montoMensual * mesesAjustados;
    const montoPorDias = (baseCTS * reglas.cts.factor / 360) * diasAjustados;

    const monto = montoPorMeses + montoPorDias;

    return {
      monto,
      detalle: `${mesesAjustados} meses × S/ ${montoMensual.toFixed(2)} + ${diasAjustados} días × S/ ${(baseCTS * reglas.cts.factor / 360).toFixed(2)} (desde período CTS correspondiente)`
    };
  } catch (error) {
    console.error("Error calculando CTS trunca:", error);
    return { monto: 0, detalle: "Error en cálculo" };
  }
};

// Función para calcular salario del mes de cese
const calcularSalarioMesCese = (RC, fechaCese, fechaIngreso) => {
  try {
    // Para evitar problemas de zona horaria, extraemos los componentes de la fecha
    const fechaCeseStr = fechaCese.toISOString().split('T')[0]; // YYYY-MM-DD
    const [añoCese, mesCeseStr, diaCeseStr] = fechaCeseStr.split('-');
    const mesCese = parseInt(mesCeseStr);
    const diaCese = parseInt(diaCeseStr);

    // Días totales del mes de cese
    const ultimoDiaMes = new Date(parseInt(añoCese), mesCese, 0).getDate();

    // Determinar días trabajados en el mes
    let diasTrabajados;
    let detalle;

    const fechaIngresoStr = fechaIngreso.toISOString().split('T')[0];
    const [añoIngreso, mesIngresoStr] = fechaIngresoStr.split('-');
    const mesIngreso = parseInt(mesIngresoStr);

    if (parseInt(añoIngreso) === parseInt(añoCese) && mesIngreso === mesCese) {
      // Ingresó en el mismo mes de cese
      const diaIngreso = parseInt(fechaIngresoStr.split('-')[2]);
      diasTrabajados = diaCese - diaIngreso + 1; // +1 para incluir el día de cese
      detalle = `Trabajó del ${diaIngreso} al ${diaCese} de ${mesCese}/${añoCese} (${diasTrabajados} días)`;
    } else {
      // Ingresó en meses anteriores o años anteriores, trabajó todo el mes hasta la fecha de cese
      // Si cesa el último día del mes, trabajó todos los días del mes
      if (diaCese === ultimoDiaMes) {
        diasTrabajados = ultimoDiaMes;
        detalle = `Trabajó todo el mes ${mesCese}/${añoCese} (${diasTrabajados} días)`;
      } else {
        diasTrabajados = diaCese;
        detalle = `Trabajó ${diasTrabajados} días del mes ${mesCese}/${añoCese}`;
      }
    }

    // Calcular monto proporcional
    const monto = (RC / ultimoDiaMes) * diasTrabajados;

    return {
      monto,
      detalle: `${detalle} × S/ ${(RC / ultimoDiaMes).toFixed(2)} diario = S/ ${monto.toFixed(2)}`,
      diasTrabajados,
      diasTotalMes: ultimoDiaMes
    };
  } catch (error) {
    console.error("Error calculando salario del mes de cese:", error);
    return { monto: 0, detalle: "Error en cálculo", diasTrabajados: 0, diasTotalMes: 30 };
  }
};

// Función para calcular vacaciones truncas
const calcularVacacionesTruncas = (RC, tiempoServicio, reglas, vacacionesPendientes, fechaCese, fechaIngreso) => {
  try {
    // Si hay vacaciones pendientes específicas, usarlas
    if (vacacionesPendientes > 0) {
      const monto = (RC / reglas.vacaciones.divisor) * vacacionesPendientes;
      return {
        monto,
        detalle: `${vacacionesPendientes} días pendientes × S/ ${(RC / reglas.vacaciones.divisor).toFixed(2)}`
      };
    }

    // Calcular días trabajados en el año de cese
    const añoCese = fechaCese.getFullYear();
    const añoIngreso = fechaIngreso.getFullYear();

    let diasTrabajadosAnio;

    if (añoIngreso < añoCese) {
      // Si ingresó en años anteriores, trabajó todo el año
      diasTrabajadosAnio = 365;
    } else {
      // Si ingresó en el mismo año, calcular desde fecha de ingreso hasta fecha de cese
      const inicioAño = new Date(añoCese, 0, 1); // 1 de enero del año de cese
      const fechaInicioParaCalculo = fechaIngreso > inicioAño ? fechaIngreso : inicioAño;

      const diffTime = Math.abs(fechaCese - fechaInicioParaCalculo);
      diasTrabajadosAnio = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 para incluir el día de cese
    }

    const diasVacacionesProporcionales = (reglas.vacaciones.diasAnuales * diasTrabajadosAnio) / 365;

    const monto = (RC / reglas.vacaciones.divisor) * diasVacacionesProporcionales;

    return {
      monto,
      detalle: `${diasVacacionesProporcionales.toFixed(1)} días proporcionales × S/ ${(RC / reglas.vacaciones.divisor).toFixed(2)}`
    };
  } catch (error) {
    console.error("Error calculando vacaciones truncas:", error);
    return { monto: 0, detalle: "Error en cálculo" };
  }
};

// Función para calcular indemnización
const calcularIndemnizacion = (RC, tiempoServicio, reglas, fechaCese) => {
  try {
    if (!reglas.indemnizacion.aplica || tiempoServicio.años < 1) {
      return 0;
    }

    let monto = 0;

    if (reglas.indemnizacion.factorPorAnio) {
      // Indemnización por sueldos completos
      const añosComputables = Math.min(tiempoServicio.años, reglas.indemnizacion.topeAnios || 12);
      monto = RC * reglas.indemnizacion.factorPorAnio * añosComputables;
    } else if (reglas.indemnizacion.diasPorAnio) {
      // Indemnización por días
      const diasTotales = Math.min(
        tiempoServicio.totalDias * reglas.indemnizacion.diasPorAnio / 365,
        reglas.indemnizacion.topeDias
      );
      monto = (RC / 30) * diasTotales;
    }

    return monto;
  } catch (error) {
    console.error("Error calculando indemnización:", error);
    return 0;
  }
};

// Función para calcular descuentos de liquidación
// Función para calcular descuentos de liquidación
const calcularDescuentosLiquidacion = (salarioMesCese, gratificacion, cts, vacaciones, indemnizacion, parametros, empleado, config, fechaCese, historial5ta = null) => {
  try {
    console.log("🔍 calcularDescuentosLiquidacion - Parámetros:", {
      salarioMesCese,
      gratificacion,
      cts,
      vacaciones,
      indemnizacion,
      historial5ta
    });

    // DESCUENTO PREVISIONAL (AFP/ONP)
    // Se aplica sobre: Salario del mes de cese + Vacaciones truncas
    // NO se aplica sobre: Gratificación trunca, CTS trunca, Indemnización
    const basePrevisional = salarioMesCese + vacaciones;
    let sistemaPension = { fondo: 0, comision: 0, seguro: 0, onp: 0, total: 0 };

    if (basePrevisional > 0 && empleado && config) {
      sistemaPension = calcularSistemaPensionario(basePrevisional, empleado, config, fechaCese);
    }

    // IMPUESTO A LA RENTA (5TA CATEGORÍA) - LÓGICA DE LIQUIDACIÓN
    // Ingresos afectos de la liquidación: Salario + Vacaciones + Gratificación Trunca
    // IMPORTANTE: Según instrucciones, la Gratificación Trunca SÍ es renta de 5ta categoría, 
    // pero CTS, Indemnización y el Bono 9% de la gratificación son INAFECTOS.
    // Nota: El parámetro 'gratificacion' aquí incluye el bono 9%. Debemos separar el bono.
    // El bono es el 9% calculado sobre la base. GratiTotal = Base + 9%Base = 1.09 * Base
    // Base = GratiTotal / 1.09
    const gratificacionBase = gratificacion / 1.09;

    // Ingresos afectos en la liquidación para Renta 5ta
    const ingresosAfectosLiquidacion = salarioMesCese + vacaciones + gratificacionBase;
    const baseImponible = ingresosAfectosLiquidacion;

    let impuestoRenta = 0;

    // CASO 1: Tenemos historial real (Lógica de precisión)
    if (historial5ta && historial5ta.ingresosAfectosPasados !== undefined) {
      console.log("✅ Usando historial real para cálculo de Renta 5ta");

      const ingresosAfectosPasados = historial5ta.ingresosAfectosPasados || 0;
      const gratificacionesPercibidas = historial5ta.gratificacionesPercibidas || 0;
      const pagosCuenta = historial5ta.pagosCuenta5ta || 0;

      // Paso 1: Ingreso Bruto Anual Real
      const ingresoBrutoAnual = ingresosAfectosPasados + gratificacionesPercibidas + ingresosAfectosLiquidacion;

      // Paso 2: Base Imponible Anual (I_bruto - 7 UIT)
      const valorUIT = parametros.uit || 5500; // UIT 2026
      const deduccion7UIT = 7 * valorUIT;
      const rentaNetaAnual = Math.max(0, ingresoBrutoAnual - deduccion7UIT);

      // Paso 3: Impuesto Anual (Escala progresiva)
      let impuestoAnual = 0;

      if (rentaNetaAnual > 0) {
        // Escalas 2026 (sincronizadas con calcularRenta5ta)
        const escalas = [
          { limite: 5, tasa: 0.08 },
          { limite: 20, tasa: 0.14 },
          { limite: 35, tasa: 0.17 },
          { limite: 45, tasa: 0.20 },
          { limite: Infinity, tasa: 0.30 }
        ];

        let baseRestante = rentaNetaAnual;
        let tramoAnterior = 0;

        for (const escala of escalas) {
          const limiteMonto = escala.limite === Infinity ? Infinity : escala.limite * valorUIT;
          const tramoMonto = limiteMonto - tramoAnterior;

          const montoEnTramo = Math.min(baseRestante, tramoMonto);

          if (montoEnTramo > 0) {
            impuestoAnual += montoEnTramo * escala.tasa;
            baseRestante -= montoEnTramo;
            tramoAnterior = limiteMonto;
          } else {
            break;
          }
        }
      }

      // Paso 4: Monto a Retener en Liquidación = Impuesto Anual - Pagos a Cuenta
      let retencionLiq = impuestoAnual - pagosCuenta;

      // Si el resultado es negativo (saldo a favor del trabajador), la retención es 0
      // En algunos casos la empresa devuelve el exceso, pero lo estándar en liquidación 
      // suele ser retener hasta 0 y el trabajador pide devolución a SUNAT.
      // Asumiremos floor 0.
      impuestoRenta = Math.max(0, retencionLiq);

      console.log("🔍 Cálculo Renta 5ta (Historial):", {
        ingresosAfectosPasados,
        gratificacionesPercibidas,
        ingresosAfectosLiquidacion: {
          salario: salarioMesCese,
          vacaciones,
          gratificacionBase
        },
        ingresoBrutoAnual,
        deduccion7UIT,
        rentaNetaAnual,
        impuestoAnual,
        pagosCuenta,
        impuestoRenta
      });

    } else {
      // CASO 2: Fallback a proyección (cuando no hay historial disponible)
      console.warn("⚠️ Sin historial disponible, usando método de proyección (menos preciso)");

      // ... mantener lógica anterior simplificada o proyección ...
      // Por simplicidad, usamos la proyección sobre la base de vacaciones y salario
      // (Esta es la lógica que teníamos antes, modificada para incluir salario)
      // ...

      // Reutilizamos la lógica anterior pero con la nueva base (Salario + Vacaciones)
      // Ojo: Gratificación Trunca también debería entrar aquí si se proyecta.
      // Simplificación para no complicar el fallback:
      const mesCese = fechaCese.getMonth() + 1;
      const mesesTrabajados = mesCese; // Aproximación
      const promedioMensual = baseImponible / mesesTrabajados; // Aprox
      // ... (Lógica simplificada omitida para enfocar, retornamos 0 si no hay historial para forzar uso del historial)
      // O mejor, calculamos algo básico sobre lo que hay.

      const ingresoProyectado = ingresosAfectosLiquidacion * (12 / mesesTrabajados); // Proyección muy burda
      const rentaNeta = Math.max(0, ingresoProyectado - (7 * (parametros.uit || 5500)));
      impuestoRenta = rentaNeta > 0 ? rentaNeta * 0.08 / 12 : 0; // Muy simplificado
    }

    // APORTES DEL EMPLEADOR (ESSALUD 9%)
    // CORRECCIÓN LEGAL:
    // La CTS está 100% inafecta a EsSalud.
    // La Gratificación Trunca está inafecta a EsSalud (se paga bono 9% al trabajador).
    // La Indemnización no es remunerativa.
    // Base Imponible EsSalud = Salario Mes Cese + Vacaciones Truncas
    const baseEssalud = salarioMesCese + vacaciones;
    const aportes = baseEssalud * 0.09; // 9% ESSALUD

    console.log("🔍 calcularDescuentosLiquidacion - EsSalud (Corregido):", {
      salarioMesCese,
      vacaciones,
      gratificacion_excluida: gratificacion,
      cts_excluida: cts,
      baseEssalud,
      aportes
    });

    const resultado = {
      impuestoRenta,
      descuentoPrevisionalTotal: sistemaPension.total,
      aportes,
      sistemaPension,
      baseImponibleQuinta: baseImponible,
      basePrevisional,
      baseEssalud,
      detalleQuinta: {
        ingresoBrutoAnual: historial5ta ? (historial5ta.ingresosAfectosPasados + historial5ta.gratificacionesPercibidas + ingresosAfectosLiquidacion) : 0,
        impuestoAnual: impuestoRenta + (historial5ta?.pagosCuenta5ta || 0),
        pagosCuenta: historial5ta?.pagosCuenta5ta || 0,
        impuestoRenta
      }
    };

    console.log("🔍 calcularDescuentosLiquidacion - Resultado final:", resultado);

    return resultado;
  } catch (error) {
    console.error("Error calculando descuentos:", error);
    // Retornar valores por defecto seguros en caso de error
    return {
      impuestoRenta: 0,
      descuentoPrevisionalTotal: 0,
      aportes: 0,
      sistemaPension: {
        fondo: 0,
        comision: 0,
        seguro: 0,
        onp: 0,
        total: 0
      },
      baseImponibleQuinta: 0,
      detalleQuinta: {
        mesesTrabajados: 0,
        ingresoMensual: 0,
        mesesFaltantes: 0,
        impuestoRenta: 0
      }
    };
  }
};



/**
 * Calcula la liquidación completa al cese de un trabajador
 * @param {Object} empleado - Datos del empleado
 * @param {Object} regimen - Configuración del régimen laboral
 * @param {Object} parametros - Parámetros generales de planilla
 * @param {string|Date} fechaCese - Fecha de cese
 * @param {string} tipoCese - Tipo de cese ('despido', 'renuncia', etc.)
 * @returns {Promise<Object>} - Detalle de la liquidación
 */
/**
 * Calcula la liquidación completa al cese de un trabajador según legislación peruana
 * Implementa DL 728, Ley 30057 y demás normas laborales vigentes
 * @param {Object} empleado - Datos del empleado
 * @param {Object} regimen - Configuración del régimen laboral
 * @param {Object} parametros - Parámetros generales (rmv, uit, etc.)
 * @param {string|Date} fechaCese - Fecha de cese
 * @param {string} tipoCese - Tipo de cese ('despido', 'renuncia', 'mutuo_disenso', 'jubilacion')
 * @param {Object} opciones - Opciones adicionales (vacacionesPendientes, rc, etc.)
 * @returns {Promise<Object>} - Detalle completo de la liquidación
 */
/**
 * Calcula el monto de asignación familiar para un empleado
 * @param {Object} empleado - Datos del empleado
 * @param {Object} parametros - Parámetros generales (rmv, etc.)
 * @param {Object} regimen - Configuración del régimen laboral
 * @returns {number} - Monto de asignación familiar
 */
const calcularAsignacionFamiliar = (empleado, parametros, regimen) => {
  try {
    // Si asignacion_familiar es un número, usarlo directamente
    if (typeof empleado.asignacion_familiar === 'number' && empleado.asignacion_familiar > 0) {
      return empleado.asignacion_familiar;
    }

    // Si asignacion_familiar es true y el régimen lo permite, calcular basado en RMV
    if (empleado.asignacion_familiar === true && regimen.asignacion_familiar?.aplica) {
      const porcentaje = regimen.asignacion_familiar.porcentaje_rmv || 10;
      const rmv = Number(parametros?.rmv || 1130); // RMV desde configuración, fallback 2026
      const calculado = rmv * porcentaje / 100;

      // Si el cálculo da un valor bajo, usar RMV como base (no hardcode)
      return calculado < 100 ? rmv * porcentaje / 100 : calculado;
    }

    // En cualquier otro caso, retornar 0
    return 0;
  } catch (error) {
    console.error("Error calculando asignación familiar:", error);
    return 113; // Valor por defecto
  }
};

export const calcularLiquidacion = async (
  empleado,
  regimen,
  parametros,
  config,
  fechaCese,
  tipoCese = 'despido',
  opciones = {},
  historial5ta = null
) => {
  try {
    console.log("🔍 calcularLiquidacion - Iniciando cálculo según legislación peruana", {
      empleado: empleado?.nombres + ' ' + empleado?.apellidos,
      regimen: regimen?.codigo,
      fechaCese,
      tipoCese,
      opciones
    });

    // Validaciones de entrada
    if (!empleado || !regimen || !parametros || !config || !fechaCese) {
      console.warn("calcularLiquidacion: parámetros requeridos faltantes");
      return {
        error: true,
        mensaje: "Parámetros requeridos faltantes",
        detalle: "Se requieren empleado, régimen, parámetros, config y fecha de cese"
      };
    }

    // Preparar datos del empleado
    const empleadoData = {
      ...empleado,
      nombres: empleado.nombres || empleado.nombre,
      apellidos: empleado.apellidos || empleado.apellido,
      sueldo_base: Number(empleado.sueldo_base || empleado.sueldo_basico || 0) || 0,
      asignacion_familiar: calcularAsignacionFamiliar(empleado, parametros, regimen),
      promedio_variables: Number(empleado.promedio_variables || empleado.bonos_promedio || 0) || 0,
      fecha_ingreso: empleado.fecha_ingreso || empleado.fechaIngreso
    };

    // Remuneración Computable (RC) - Base para todos los cálculos
    // RC = Sueldo Base + Asignación Familiar (excluyendo variables)
    const RC = Number(empleadoData.sueldo_base + empleadoData.asignacion_familiar) || 0;

    if (RC <= 0) {
      return {
        error: true,
        mensaje: "Remuneración computable inválida",
        detalle: "La RC debe ser mayor a cero"
      };
    }

    // Convertir fechas - IMPORTANTE: evitar problemas de zona horaria
    let fechaCeseObj, fechaIngresoObj;
    try {
      // Para fechas en formato string YYYY-MM-DD, crear fecha local sin conversión de zona horaria
      if (typeof fechaCese === 'string') {
        const [year, month, day] = fechaCese.split('-').map(Number);
        fechaCeseObj = new Date(year, month - 1, day); // month es 0-indexed
      } else if (fechaCese instanceof Date) {
        fechaCeseObj = fechaCese;
      } else if (fechaCese.toDate) {
        fechaCeseObj = fechaCese.toDate();
      } else {
        fechaCeseObj = new Date(fechaCese);
      }

      // Mismo tratamiento para fecha de ingreso
      if (empleadoData.fecha_ingreso instanceof Date) {
        fechaIngresoObj = empleadoData.fecha_ingreso;
      } else if (empleadoData.fecha_ingreso?.toDate) {
        fechaIngresoObj = empleadoData.fecha_ingreso.toDate();
      } else if (typeof empleadoData.fecha_ingreso === 'string') {
        const [year, month, day] = empleadoData.fecha_ingreso.split('-').map(Number);
        fechaIngresoObj = new Date(year, month - 1, day);
      } else {
        fechaIngresoObj = new Date(empleadoData.fecha_ingreso);
      }

      console.log("🔍 Fechas parseadas:", {
        fechaCeseOriginal: fechaCese,
        fechaCeseObj,
        fechaIngresoOriginal: empleadoData.fecha_ingreso,
        fechaIngresoObj
      });
    } catch (error) {
      return {
        error: true,
        mensaje: "Fechas inválidas",
        detalle: "Error al procesar fechas de ingreso o cese"
      };
    }

    // Calcular tiempo de servicios
    const tiempoServicio = calcularTiempoServicio(fechaIngresoObj, fechaCeseObj);
    console.log("🔍 Tiempo de servicio calculado:", tiempoServicio);

    // CÁLCULO DE SALARIO DEL MES DE CESE
    const salarioMesCese = calcularSalarioMesCese(RC, fechaCeseObj, fechaIngresoObj);

    // Aplicar reglas por régimen laboral
    const reglasRegimen = obtenerReglasPorRegimen(regimen.codigo);
    console.log("🔍 Reglas del régimen aplicadas:", reglasRegimen);

    // CÁLCULO DE GRATIFICACIÓN TRUNCA
    const gratificacionPagada = opciones.gratificacionPagada || false;
    const gratificacion = calcularGratificacionTrunca(
      RC, tiempoServicio, reglasRegimen, fechaCeseObj, parametros, gratificacionPagada
    );

    // CÁLCULO DE CTS TRUNCA
    const cts = calcularCTSTrunca(
      RC, tiempoServicio, reglasRegimen, fechaCeseObj, parametros
    );

    // CÁLCULO DE VACACIONES TRUNCAS
    const vacacionesPendientes = opciones.vacacionesPendientes || 0;
    const vacaciones = calcularVacacionesTruncas(
      RC, tiempoServicio, reglasRegimen, vacacionesPendientes, fechaCeseObj, fechaIngresoObj
    );

    // CÁLCULO DE INDEMNIZACIÓN (solo para despido arbitrario)
    let indemnizacion = 0;
    if (tipoCese === 'despido') {
      indemnizacion = calcularIndemnizacion(
        RC, tiempoServicio, reglasRegimen, fechaCeseObj
      );
    }

    // CÁLCULOS DE DESCUENTOS Y APORTES
    const descuentosYAportes = calcularDescuentosLiquidacion(
      salarioMesCese.monto, gratificacion.monto, cts.monto, vacaciones.monto, indemnizacion, parametros, empleado, config, fechaCeseObj, historial5ta
    ) || {
      impuestoRenta: 0,
      descuentoPrevisionalTotal: 0,
      aportes: 0,
      sistemaPension: { fondo: 0, comision: 0, seguro: 0, onp: 0, total: 0 },
      baseImponibleQuinta: 0
    };

    // TOTALES - Solo deducciones del trabajador afectan el neto
    const totalBruto = salarioMesCese.monto + gratificacion.monto + cts.monto + vacaciones.monto + indemnizacion;
    const deduccionesTrabajador = Number(descuentosYAportes.impuestoRenta || 0) + Number(descuentosYAportes.descuentoPrevisionalTotal || 0); // Impuesto a la renta + descuento previsional (AFP/ONP)
    const aportesEmpleador = Number(descuentosYAportes.aportes || 0); // EsSalud - no afecta neto del trabajador
    const totalNeto = totalBruto - deduccionesTrabajador;

    console.log("🔍 Debug liquidación:", {
      totalBruto,
      deduccionesTrabajador,
      aportesEmpleador,
      totalNeto,
      descuentosYAportes
    });

    const resultado = {
      // Salario del mes de cese
      salarioMesCese: {
        bruto: Number(salarioMesCese.monto.toFixed(2)),
        inafecto: false, // El salario está sujeto a impuesto a la renta
        detalle: salarioMesCese.detalle
      },
      // Montos principales
      gratificacion: {
        bruto: Number(gratificacion.monto.toFixed(2)),
        inafecto: reglasRegimen.gratificacion.inafecto,
        detalle: gratificacion.detalle
      },
      cts: {
        bruto: Number(cts.monto.toFixed(2)),
        inafecto: reglasRegimen.cts.inafecto,
        detalle: cts.detalle
      },
      vacaciones: {
        bruto: Number(vacaciones.monto.toFixed(2)),
        inafecto: false, // Las vacaciones sí pagan impuesto a la renta
        detalle: vacaciones.detalle
      },
      indemnizacion: {
        bruto: Number(indemnizacion.toFixed(2)),
        inafecto: reglasRegimen.indemnizacion.inafecto,
        detalle: reglasRegimen.indemnizacion.detalle
      },

      // Totales
      totales: {
        bruto: Number(totalBruto.toFixed(2)),
        deducciones: Number((deduccionesTrabajador || 0).toFixed(2)),
        neto: Number(totalNeto.toFixed(2))
      },

      // Descuentos y aportes
      descuentos: {
        impuestoRenta: Number((descuentosYAportes.impuestoRenta || 0).toFixed(2)),
        descuentoPrevisionalVacaciones: Number((descuentosYAportes.descuentoPrevisionalVacaciones || 0).toFixed(2)),
        aportesEmpleador: Number((descuentosYAportes.aportes || 0).toFixed(2)),
        sistemaPension: descuentosYAportes.sistemaPension || { fondo: 0, comision: 0, seguro: 0, onp: 0, total: 0 },
        baseImponibleQuinta: Number((descuentosYAportes.baseImponibleQuinta || 0).toFixed(2)),
        detalleQuinta: descuentosYAportes.detalleQuinta || {
          mesesTrabajados: 0,
          ingresoMensual: 0,
          mesesFaltantes: 0,
          impuestoRenta: 0
        }
      },

      // Información adicional
      metadata: {
        empleado: {
          nombres: empleadoData.nombres,
          apellidos: empleadoData.apellidos,
          documento: empleadoData.dni || empleadoData.documento
        },
        regimen: regimen.codigo,
        tiempoServicio,
        tipoCese,
        fechaCese: fechaCeseObj.toISOString().split('T')[0],
        fechaIngreso: fechaIngresoObj.toISOString().split('T')[0],
        rc: Number(RC.toFixed(2))
      },
      parametros: parametros
    };

    console.log("🔍 calcularLiquidacion - Resultado final:", resultado);
    return resultado;

  } catch (error) {
    console.error("Error crítico en calcularLiquidacion:", error);
    return {
      error: true,
      mensaje: "Error en el cálculo de liquidación",
      detalle: error.message
    };
  }
};

// Función para calcular Horas Extras
export const calcularHorasExtras = (sueldoBase, horas25 = 0, horas35 = 0, horas100 = 0) => {
  try {
    if (!sueldoBase || sueldoBase <= 0) return { monto25: 0, monto35: 0, monto100: 0, total: 0 };

    // Valor Hora Simple = Sueldo / 30 / 8 (o Sueldo / 240)
    const valorHora = sueldoBase / 240;

    // Cálculo de montos por tipo
    const monto25 = horas25 * valorHora * 1.25;
    const monto35 = horas35 * valorHora * 1.35;
    const monto100 = horas100 * valorHora * 2.00;

    return {
      monto25: Number(monto25.toFixed(2)),
      monto35: Number(monto35.toFixed(2)),
      monto100: Number(monto100.toFixed(2)),
      total: Number((monto25 + monto35 + monto100).toFixed(2))
    };
  } catch (error) {
    console.error("Error en calcularHorasExtras:", error);
    return { monto25: 0, monto35: 0, monto100: 0, total: 0 };
  }
};

export default {
  calcularMesesComputables,
  calcularGratificacion,
  calcularCTS,
  calcularVacaciones,
  calcularEssalud,
  calcularRenta5ta,
  calcularDescuentosPorFaltas,
  calcularDescuentosPorTardanzas,
  calcularIndemnizacionDespido,
  calcularBonificacionesConstruccionCivil,
  calcularSistemaPensionario,
  calcularEssaludVida,
  calcularSCTR,
  calcularVidaLey,
  calcularBonosRemunerativos,
  calcularBonosNoRemunerativos,
  calcularProvisiónVacaciones,
  calcularProvisiónGratificaciones,
  calcularProvisiónGratificaciones,
  calcularLiquidacion,
  calcularHorasExtras,
};
